import { cn } from '@/lib/utils';

/**
 * Colour scheme for a sports pill. Derived from the outcome side: the away/over
 * leg is purple, the home/under leg is blue, and a draw is grey — matching the
 * Crown Prediction sports design (Figma 5138 / 5379). The fills are token-driven
 * utilities (`bg-gradient-sports-*`) defined in `index.css`.
 */
export type SportsPillTone = 'purple' | 'blue' | 'grey';

const TONE_CLASS: Record<SportsPillTone, string> = {
  purple: 'bg-gradient-sports-purple',
  blue: 'bg-gradient-sports-blue',
  grey: 'bg-sports-draw',
};

export interface SportsOutcomePillProps {
  /** Colour scheme (by side — away/over→purple, home/under→blue, draw→grey). */
  tone: SportsPillTone;
  /** Primary label — team name / abbreviation, "Over"/"Under", etc. Ellipsised if it can't fit. */
  label: string;
  /** Secondary label appended to the primary one, e.g. a spread/total line ("-5.5"). */
  lineLabel?: string | null;
  /** Whole-percent chance shown on the pill; renders "—" when null. */
  percent: number | null;
  /**
   * `moneyline` centres "Label pct%" on one line; `line` splits the label (+line)
   * to the left and the percent to the right (spread/total pills).
   */
  variant?: 'moneyline' | 'line';
  onClick?: () => void;
  className?: string;
}

/**
 * A branded sports outcome pill — the tappable team/side button used by the
 * moneyline card and the spread/total ladders. Gradient + inset glow, white text,
 * label 20px / percent 14px per the Figma "Button - Big" (node 4169:103845).
 *
 * The percent is a `shrink-0` sibling so it is always visible; the label truncates
 * with an ellipsis when it can't fit. (Figma sizes these pills for 3-letter team
 * abbreviations; the Plaee feed only provides full team names, so long names
 * ellipsise — see the sports plan's abbreviation gap.)
 */
export function SportsOutcomePill({
  tone,
  label,
  lineLabel,
  percent,
  variant = 'moneyline',
  onClick,
  className,
}: SportsOutcomePillProps) {
  const pct = percent == null ? '—' : `${percent}%`;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative flex h-[50px] w-full items-center overflow-hidden rounded-pill px-3 text-white capitalize',
        'shadow-[0px_1.818px_1.818px_0px_rgba(0,0,0,0.15),inset_0px_2px_4px_0px_rgba(255,255,255,0.3)]',
        'cursor-pointer border-none',
        TONE_CLASS[tone],
        variant === 'moneyline' ? 'justify-center' : 'justify-between gap-2',
        className,
      )}
    >
      <span className="flex min-w-0 items-baseline gap-1">
        <span className="min-w-0 truncate text-[20px] font-semibold leading-normal">{label}</span>
        {lineLabel && <span className="shrink-0 text-sm font-semibold leading-normal">{lineLabel}</span>}
        {variant === 'moneyline' && <span className="shrink-0 text-sm font-semibold leading-normal">{pct}</span>}
      </span>
      {variant === 'line' && <span className="shrink-0 text-[20px] font-semibold leading-normal">{pct}</span>}
    </button>
  );
}
